import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../util/ApiHelper";
import { toast } from "react-toastify";
import editorConfiguration from "../../../util/EditorConfiguration";
import { useLoading } from "../../../util/LoadingProvider";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Link } from "react-router-dom";

const schema = yup.object().shape({
  categoryId: yup
    .number()
    .typeError("Please select a category")
    .required("Please select a category"),
  titleEn: yup.string().nullable(),
  titleTc: yup.string().nullable(),
  contentEn: yup.string().nullable(),
  contentTc: yup.string().nullable(),
  showHomepage: yup.boolean().nullable(),
  status: yup.string().nullable(),
});

const defaultFormValues = {
  categoryId: null,
  titleEn: "",
  titleTc: "",
  contentEn: "",
  contentTc: "",
  showHomepage: false,
  status: "ACTIVE",
};

// Create a custom editor configuration for FAQ with membership upload
const faqEditorConfig = {
  ...editorConfiguration,
  site: 'membership',
  folder: 'FAQ',
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",
    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    padding: theme.spacing(3),
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "40px",
    textAlign: "left",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionText: {
    fontSize: "13px",
    fontWeight: 700,
    paddingLeft: "4px",
    marginBottom: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));

export default function FaqEditingPage({ disabled, ...props }) {
  const { match, history } = props;
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const [categories, setCategories] = useState([]);
  const { setLoading } = useLoading();
  const classes = useStyles();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    console.log("Form submitted with values:", value);
    console.log("Current id:", id);
    // Check if we're in create mode (id is undefined or "create")
    if (!id || id === "create") {
      createFaq(value);
    } else {
      updateFaq(value);
    }
  };

  const onError = (errors) => {
    console.log("Form validation errors:", errors);
    // Show specific error messages if available
    const errorMessages = Object.keys(errors).map(key => {
      return errors[key]?.message || `${key} is invalid`;
    });
    if (errorMessages.length > 0) {
      toast.error(errorMessages[0]);
    } else {
      toast.error("Please fix the form errors before submitting.");
    }
  };

  const getFaqCategories = useCallback(async () => {
    try {
      const result = await apiHelper.cineclub.getMembershipFaqCategory();
      if (result.data) {
        setCategories(result.data);
      }
    } catch (err) {
      console.error("Error fetching FAQ categories:", err);
      toast.error("Failed to load FAQ categories.");
    }
  }, []);

  const getFaq = useCallback(async () => {
    // Don't fetch if id is "create" or undefined
    if (!id || id === "create") {
      return;
    }
    
    setLoading(true);
    try {
      const result = await apiHelper.cineclub.getMembershipFaqById(id);
      console.log(result);
      setDefaultValues(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load FAQ.");
    }
    setLoading(false);
  }, [id, setLoading, reset]);

  const updateFaq = async (value) => {
    try {
      setLoading(true);
      console.log("Updating FAQ with data:", value);
      console.log("Category ID type:", typeof value.categoryId, "value:", value.categoryId);
      // Ensure categoryId is a number and status has a default value
      const submitData = {
        ...value,
        categoryId: value.categoryId ? Number(value.categoryId) : value.categoryId,
        status: value.status || "ACTIVE"
      };
      console.log("Submitting with categoryId:", submitData.categoryId);
      const result = await apiHelper.cineclub.updateMembershipFaq(id, submitData);
      console.log("Update result:", result);
      
      if (result.status === 200 || result.status === 201) {
        toast.dark("Your changes are saved.");
        // Refresh the FAQ data
        await getFaq();
      } else {
        toast.error("Failed to update FAQ. Please try again.");
      }
    } catch (err) {
      console.error("Error updating FAQ:", err);
      toast.error("Failed to update FAQ. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const createFaq = async (value) => {
    try {
      setLoading(true);
      console.log("Creating FAQ with data:", value);
      console.log("Category ID type:", typeof value.categoryId, "value:", value.categoryId);
      // Ensure categoryId is a number and status has a default value
      const submitData = {
        ...value,
        categoryId: value.categoryId ? Number(value.categoryId) : value.categoryId,
        status: value.status || "ACTIVE"
      };
      console.log("Submitting with categoryId:", submitData.categoryId);
      const result = await apiHelper.cineclub.createMembershipFaq(submitData);
      console.log("Create result:", result);
      
      if (result.status === 200 || result.status === 201) {
        // Extract the ID from the response - check different possible response structures
        const faqId = result.data?.id || 
                      result.data?.data?.id || 
                      (result.data?.data && typeof result.data.data === 'object' && result.data.data.id) ||
                      (result.data && result.data.code === 0 && result.data.data?.id);
        
        if (faqId) {
          toast.dark("The FAQ is created.");
          history.push(`/cineclub/faq/${faqId}/edit`);
        } else {
          console.log("Could not extract FAQ ID from response, redirecting to list:", result.data);
          toast.dark("The FAQ is created.");
          // Redirect to listing page
          history.push(`/cineclub/faq`);
        }
      } else {
        toast.error("Failed to create FAQ. Please try again.");
      }
    } catch (err) {
      console.error("Error creating FAQ:", err);
      toast.error("Failed to create FAQ. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch categories on component mount
    getFaqCategories();
  }, [getFaqCategories]);

  useEffect(() => {
    // Only fetch FAQ if we have a valid id (not in create mode)
    if (id && id !== "create") {
      getFaq();
    }
  }, [getFaq, id]);

  const watchedValues = watch();

  return (
    <div className={classes.content}>
      <Box className={classes.header}>
        <IconButton
          component={Link}
          to="/cineclub/faq"
          className={classes.backButton}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          FAQ &gt; {(!id || id === "create") ? "Add New FAQ" : "Edit FAQ"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <fieldset disabled={disabled} className="fieldset">
          <Grid container xs={12} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="categoryId"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.textInput}
                    error={!!error}
                  >
                    <InputLabel id="category-select-label">Category</InputLabel>
                    <Select
                      labelId="category-select-label"
                      id="category-select"
                      label="Category"
                      value={field.value !== null && field.value !== undefined ? String(field.value) : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Convert to number if not empty, otherwise set to undefined for validation
                        // This ensures categoryId is submitted as a number (category.id)
                        const numValue = value === "" ? undefined : Number(value);
                        field.onChange(numValue);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      disabled={disabled}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <em>Select a category</em>
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={String(category.id)}>
                          {category.nameEn || category.nameTc || `Category ${category.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && (
                      <Typography variant="caption" color="error" style={{ marginTop: "4px", marginLeft: "14px" }}>
                        {error.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="titleEn"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Title (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="titleTc"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Title (Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>

          {defaultValues && (
            <>
              <Box
                fontWeight={900}
                fontSize={13}
                mb={2}
                style={{ marginTop: "30px" }}
                width="100%"
              >
                <Typography variant="p">Content (English)</Typography>
              </Box>

              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="contentEn"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={faqEditorConfig}
                        data={field.value || ""}
                        disabled={disabled}
                        onReady={(editor) => {
                          console.log("Editor is ready to use!", editor);
                          editor.setData(defaultValues.contentEn || "");
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        onBlur={(event, editor) => {
                          console.log("Blur.", editor);
                        }}
                        onFocus={(event, editor) => {
                          console.log("Focus.", editor);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Box fontWeight={900} fontSize={13} mt={4} mb={2} width="100%">
                <Typography variant="p">Content (Chinese)</Typography>
              </Box>

              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="contentTc"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={faqEditorConfig}
                        data={field.value || ""}
                        disabled={disabled}
                        onReady={(editor) => {
                          console.log("Editor is ready to use!", editor);
                          editor.setData(defaultValues.contentTc || "");
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        onBlur={(event, editor) => {
                          console.log("Blur.", editor);
                        }}
                        onFocus={(event, editor) => {
                          console.log("Focus.", editor);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Grid
            container
            item
            xs={12}
            spacing={3}
            style={{ marginTop: "32px", marginLeft: 0 }}
          >
            <Grid item xs={12}>
              <Controller
                control={control}
                name="showHomepage"
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value || false}
                        onChange={(event) => {
                          onChange(event.target.checked);
                        }}
                        color="primary"
                        {...rest}
                      />
                    }
                    label="Show on Home Page"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid
            container
            item
            xs={12}
            spacing={3}
            style={{ marginTop: "32px", marginLeft: 0 }}
          >
            <Grid item xs={3} style={{ paddingLeft: "4px" }}>
              <Controller
                control={control}
                name="status"
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <ToggleButtonGroup
                    {...rest}
                    value={value || "ACTIVE"}
                    onChange={(event, newFormats) => {
                      onChange(newFormats);
                    }}
                    exclusive
                    aria-label="Status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value="ACTIVE"
                      aria-label="Active"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value="INACTIVE"
                      aria-label="Inactive"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Inactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
          </Grid>

        </fieldset>
        
        {!disabled && (
          <div className={classes.buttonGroup}>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              style={{
                color: "white",
                background: "#306F9D",
                marginRight: "5px",
                padding: "9px 16px",
              }}
            >
              Publish
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
